import React, { useContext, useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import { H5, P } from "../../atoms/Texts";
import { CheckBox, DarkFormField, DarkTextArea } from "../../atoms/Inputs";
import organization_categories from "../../../shared/fixtures/organization_categories";
import languages from "../../../shared/fixtures/languages";
import { NewProgramFooter } from "../../organisms/Footers/footer.new.program";
import { selectStyle } from "../../../shared/fixtures/select_style";
import eligibilities from "../../../shared/fixtures/eligibilities";

const AddProgramFormTwo = ({
  changeStep,
  discard,
  fields,
  updateFields,
  saveToDraft,
}) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const {
    register,
    watch,
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { errors: formStateErrors },
  } = useForm();

  const selectedCategory = watch("opCategory");

  const arrangeCategories = () => {
    let categories = [];
    organization_categories.forEach((_category) => {
      categories.push({
        label: _category.category,
        value: _category.category,
      });
    });
    return categories;
  };

  const [categories] = useState(arrangeCategories());

  const subCategories = useMemo(() => {
    if (!selectedCategory?.value) return [];

    const selectedCat = organization_categories.find(
      (cat) => cat.category === selectedCategory.value,
    );

    return (
      selectedCat?.sub_categories.map((subCat) => ({
        label: subCat,
        value: subCat,
      })) || []
    );
  }, [selectedCategory, organization_categories]);

  // Handle type selection

  const handleCategoryChange = (selectedOption, onChange) => {
    onChange(selectedOption);
    // Reset the classification when type changes
    setValue("subOpCategory", "");
  };

  const updateData = (data) => {
    updateFields(data);
    changeStep(3);
  };

  return (
    <main className="">
      <form onSubmit={handleSubmit(updateData)}>
        <section className="pb-20 w-10/12 organization-container">
          {/* Accreditations */}
          <div>
            <DarkTextArea
              rows={3}
              id="accreditations"
              name="accreditations"
              placeholder="What accreditations does this service have?"
              label="Accreditations"
              containerClass="flex-grow"
              defaultValue={fields.accreditations || ""}
              {...register("accreditations")}
            />
          </div>
          {/* Licenses */}
          <div>
            <DarkTextArea
              rows={3}
              id="licenses"
              name="licenses"
              placeholder="What licenses do you have for this service?"
              label="Licenses"
              containerClass="flex-grow"
              defaultValue={fields.licenses || ""}
              {...register("licenses")}
            />
          </div>
          {/* Required Documents */}
          <div>
            <DarkFormField
              type="text"
              id="requiredDocuments"
              name="requiredDocuments"
              placeholder="Documents applicants should provide when applying..."
              label="Required Documents"
              containerClass="flex-grow"
              defaultValue={fields.requiredDocuments || ""}
              {...register("requiredDocuments")}
            />
          </div>
          {/* Payments Accepted  */}
          <div>
            <DarkTextArea
              rows={3}
              id="paymentsAccepted"
              name="paymentsAccepted"
              placeholder="If you accept payments, enter the kinds of payments you accept..."
              label="Payments Accepted"
              className="w-full"
              defaultValue={fields.paymentAccepted || ""}
              {...register("paymentsAccepted")}
            />
          </div>
          {/* Fundings  */}
          <div>
            <DarkTextArea
              rows={3}
              id="fundings"
              name="fundings"
              placeholder="How is the service funded?"
              label="Fundings"
              className="w-full"
              defaultValue={fields.fundings || ""}
              {...register("fundings")}
            />
          </div>
          {/* Service Areas  */}
          <div>
            <DarkTextArea
              rows={3}
              id="serviceAreas"
              name="serviceAreas"
              placeholder="Enter the areas you offer this service..."
              label="Service Areas"
              className="w-full"
              required
              defaultValue={fields.serviceAreas || ""}
              {...register("serviceAreas", {})}
            />
            {formStateErrors.serviceAreas && (
              <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm">
                {formStateErrors.serviceAreas.message}
              </P>
            )}
          </div>
          {/* Eligibities */}
          <div className="mb-4">
            <label htmlFor="eligibilities">
              <P fontSize="14px" fontFamily="semi" color="#47495C" className="mb-2">
                Eligibilities
                <span
                  className=""
                  style={{
                    color: "#E62250",
                  }}
                >
                  *
                </span>
              </P>
            </label>
            <Controller
              control={control}
              id="eligibilities"
              name="eligibilities"
              render={({ field }) => (
                <CreatableSelect
                  {...field}
                  styles={selectStyle}
                  isMulti
                  autoComplete="true"
                  options={eligibilities}
                  placeholder="-- Type to enter eligibility --"
                  className="w-full"
                />
              )}
              defaultValue={fields.eligibilities || ""}
            />
            {formStateErrors.eligibilities && (
              <P fontSize="12px" className="mt-2 mb-2 text-red-400 text-sm">
                Enter an eligibity criteria
              </P>
            )}
          </div>
          {/* Auto Screen */}
          <div className="mb-6">
            <CheckBox
              id="autoScreen"
              name="autoScreen"
              label="Auto-screen?"
              labelColor="#413E53"
              labelClassName="ml-2"
              labelFontSize="14px"
              dedaultChecked={fields.autoScreen || false}
              {...register("autoScreen")}
            />
          </div>
          <section className="grid grid-cols-1 md:grid-cols-2 gap-x-4 mt-10">
            {/* Category */}
            <div className="mb-6">
              <label htmlFor="opCategory">
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mb-2"
                >
                  Category
                  <span
                    className=""
                    style={{
                      color: "#E62250",
                    }}
                  >
                    *
                  </span>
                </P>
              </label>
              <Controller
                control={control}
                id="opCategory"
                name="opCategory"
                render={({ field }) => (
                  <Select
                    {...field}
                    styles={selectStyle}
                    autoComplete="true"
                    options={categories}
                    onChange={(option) =>
                      handleCategoryChange(option, field.onChange)
                    }
                    placeholder="--Select a category--"
                    className="w-full"
                  />
                )}
                defaultValue={fields.opCategory?.value || ""}
              />
              {formStateErrors.opCategory && (
                <P fontSize="12px" className="mt-2 mb-2 text-red-400 text-sm">
                  Select a category
                </P>
              )}
            </div>

            {/* SubCategory */}
            <div className="mb-6">
              <label htmlFor="subOpCategory">
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mb-2"
                >
                  Subcategory
                  <span
                    className=""
                    style={{
                      color: "#E62250",
                    }}
                  >
                    *
                  </span>
                </P>
              </label>
              <Controller
                control={control}
                id="subOpCategory"
                name="subOpCategory"
                render={({ field }) => (
                  <Select
                    {...field}
                    styles={selectStyle}
                    autoComplete="true"
                    options={subCategories}
                    isDisabled={!selectedCategory}
                    placeholder={
                      !selectedCategory
                        ? "You have to select a category first"
                        : "--Select a sub category"
                    }
                    className="w-full"
                  />
                )}
                defaultValue={fields.subOpCategory?.value || ""}
              />
              {formStateErrors.subOpCategory && (
                <P fontSize="12px" className="mt-2 mb-2 text-red-400 text-sm">
                  Select a sub category
                </P>
              )}
            </div>
          </section>
          {/* Languages */}
          <div className="mb-6">
            <label htmlFor="languages">
              <P fontSize="14px" fontFamily="semi" color="#47495C" className="mb-2">
                Language
                <span
                  className=""
                  style={{
                    color: "#E62250",
                  }}
                >
                  *
                </span>
              </P>
            </label>

            <Controller
              control={control}
              id="languages"
              name="languages"
              render={({ field }) => (
                <Select
                  {...field}
                  styles={selectStyle}
                  autoComplete="true"
                  options={languages}
                  placeholder="-- Select language --"
                  className="w-full"
                />
              )}
              defaultValue={fields.languages || ""}
            />
            {formStateErrors.languages && (
              <P fontSize="12px" className="mt-2 mb-2 text-red-400 text-sm">
                Select a language
              </P>
            )}
          </div>
          {/* Contact */}
          <H5 className="mb-4 mt-16">Contact Information</H5>
          <div className="flex-grow grid grid-cols-1 md:grid-cols-2 gap-x-5">
            <div key={`contact-firstName`}>
              <div className="flex items-center justify-between">
                <DarkFormField
                  type="text"
                  id={`contact-firstName`}
                  name={`contact-firstName`}
                  placeholder="Susan"
                  label="First Name"
                  containerClass="flex-grow"
                  required
                  {...register("contact-firstName", {})}
                  defaultValue={fields["contact-firstName"] || ""}
                />
              </div>

              <ErrorMessage
                errors={formStateErrors}
                name={`contact-firstName`}
                as={
                  <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm" />
                }
              >
                {({ messages }) =>
                  messages &&
                  Object.entries(messages).map(([type, message]) => (
                    <P key={type}>{message}</P>
                  ))
                }
              </ErrorMessage>
            </div>

            <div key={`contact-lastName`}>
              <div className="flex items-center justify-between">
                <DarkFormField
                  type="text"
                  id={`contact-lastName`}
                  name={`contact-lastName`}
                  placeholder="Markle"
                  label="Last Name"
                  containerClass="flex-grow"
                  required
                  {...register("contact-lastName", {})}
                  defaultValue={fields["contact-lastName"] || ""}
                />
              </div>

              <ErrorMessage
                errors={formStateErrors}
                name={`contact-lastName`}
                as={
                  <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm" />
                }
              >
                {({ messages }) =>
                  messages &&
                  Object.entries(messages).map(([type, message]) => (
                    <P key={type}>{message}</P>
                  ))
                }
              </ErrorMessage>
            </div>

            <div key={`contact-title`}>
              <div className="flex items-center justify-between">
                <DarkFormField
                  type="text"
                  id={`contact-title`}
                  name={`contact-title`}
                  placeholder="Mr, Mrs, Dr"
                  label="Title"
                  containerClass="flex-grow"
                  defaultValue={fields["contact-title"] || ""}
                  {...register("contact-title")}
                />
              </div>
            </div>

            <div key={`contact-department`}>
              <div className="flex items-center justify-between">
                <DarkFormField
                  type="text"
                  id={`contact-department`}
                  name={`contact-department`}
                  placeholder="Enter department"
                  label="Department"
                  containerClass="flex-grow"
                  {...register("contact-department")}
                  defaultValue={fields["contact-department"] || ""}
                />
              </div>
            </div>

            <div key={`contact-email`}>
              <div className="flex items-center justify-between">
                <DarkFormField
                  type="text"
                  id={`contact-email`}
                  name={`contact-email`}
                  placeholder="example@gmail.com"
                  label="Email"
                  defaultValue={fields["contact-email"] || ""}
                  containerClass="flex-grow"
                  required
                  {...register("contact-email", {
                    pattern: {
                      value:
                        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                      message: "Please enter a valid email.",
                    },
                  })}
                />
              </div>

              <ErrorMessage
                errors={formStateErrors}
                name={`contact-email`}
                as={
                  <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm" />
                }
              >
                {({ messages }) =>
                  messages &&
                  Object.entries(messages).map(([type, message]) => (
                    <P key={type}>{message}</P>
                  ))
                }
              </ErrorMessage>
            </div>
          </div>{" "}
        </section>
        <NewProgramFooter
          changeStep={updateFields}
          updateFields={updateFields}
          discard={discard}
          saveToDraft={() => {
            const values = getValues();
            saveToDraft(values);
          }}
        />
      </form>
    </main>
  );
};

export default AddProgramFormTwo;
